import type { OfficeHost } from '@/taskpane/services/OfficeHostService';
import { ThemeService, type ThemeOption } from '@/taskpane/services/ThemeService';
import { LogOut, User } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@docuid/ui/components/ui/dropdown-menu';

interface HeaderProperties {
  user: { name?: string; phone: string; email?: string } | null;
  onLogout: () => void;
  onNavigateToProfile?: () => void;
  officeHost?: OfficeHost;
}

const Header: React.FC<HeaderProperties> = ({
  user,
  onLogout,
  onNavigateToProfile,
  officeHost,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [themeState, setThemeState] = useState<ThemeOption>(ThemeService.getResolvedTheme());

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase();
  };

  const truncateEmail = (email: string) => {
    if (email.length <= 20) return email;
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 12) {
      return `${localPart}@${domain.slice(0, 8)}...`;
    }
    return `${localPart.slice(0, 12)}...@${domain.slice(0, 8)}...`;
  };

  const displayName = user?.name ? user?.name : 'User';
  const displayEmail = user?.email || '';

  const getHostBackground = (): string => 'linear-gradient(135deg, #26251e 0%, #3a3832 100%)';

  return (
    <div className="px-5 py-4 text-white select-none" style={{ background: getHostBackground() }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="dark:bg-muted/20 dark:border-border/30 flex items-center justify-center rounded-xl border border-white/20 bg-white/10 p-1.5 shadow-inner backdrop-blur-sm">
            <img
              src="assets/logo-transparent.png"
              alt="iVALT DocuID"
              className="h-[28px] w-[28px] object-contain dark:brightness-0 dark:invert"
            />
          </div>
          <div className="flex flex-col">
            <h1 className="m-0 text-xl font-semibold tracking-tight text-white">DocuID</h1>
            <span className="mt-0.5 flex items-center gap-1.5 text-[10px] text-white/70">
              Powered by iVALT
              {officeHost && officeHost !== 'Unknown' && (
                <span
                  className="inline-flex items-center rounded-full border border-white/30 bg-white/20 px-1.5 py-[1px] text-[9px] font-semibold tracking-wider text-white uppercase"
                  title={`Running in Microsoft ${officeHost}`}
                >
                  {officeHost}
                </span>
              )}
            </span>
          </div>
        </div>

        {user && (
          <div className="flex items-center gap-4">
            <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <button className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 transition-colors hover:bg-white/10">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-sm font-semibold text-white">
                    {getInitials(user?.name || 'User')}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-white">{displayName}</span>
                    <span className="text-xs text-white/80">{truncateEmail(displayEmail)}</span>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-2">
                <div className="border-border mb-2 flex items-center gap-3 border-b pb-2">
                  <div className="bg-muted text-foreground flex h-10 w-10 items-center justify-center rounded-full font-semibold">
                    {displayName.charAt(0)}
                  </div>
                  <div>
                    <p className="text-foreground text-sm font-semibold">{displayName}</p>
                    <p className="text-muted-foreground text-xs">{displayEmail}</p>
                  </div>
                </div>
                {onNavigateToProfile && (
                  <DropdownMenuItem
                    onClick={() => {
                      onNavigateToProfile();
                      setIsDropdownOpen(false);
                    }}
                  >
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                )}

                {/* Theme Switch Selector */}
                <div className="border-border mb-1.5 flex flex-col gap-1.5 border-b px-2 py-1.5 select-none">
                  <span className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
                    Theme
                  </span>
                  <div className="flex gap-1">
                    {(['light', 'dark', 'system'] as const).map((option) => (
                      <button
                        key={option}
                        onClick={(e) => {
                          e.stopPropagation();
                          ThemeService.setTheme(option);
                          setThemeState(option);
                        }}
                        className={`flex-1 cursor-pointer rounded-md border py-1 text-[11px] font-semibold capitalize transition-all ${
                          themeState === option
                            ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
                            : 'bg-muted text-muted-foreground border-border hover:bg-accent/10 hover:text-foreground'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                <DropdownMenuItem onClick={onLogout} variant="destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
